import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Mail,
  IdCard,
  FileCheck2,
  Crown,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Upload,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ArtBackdrop from "@/components/ArtBackdrop";
import { apiClient, formatApiError } from "@/lib/api";

const STEPS = [
  { id: "intro", label: "Bienvenue", icon: ShieldCheck },
  { id: "email", label: "Vérification email", icon: Mail },
  { id: "identity", label: "Identité pro", icon: IdCard },
  { id: "justificatif", label: "Justificatif", icon: FileCheck2 },
  { id: "plan", label: "Plan d'affiliation", icon: Crown },
  { id: "review", label: "Validation", icon: Check },
];

const PLAN_DEFS = [
  {
    key: "evaluation_libre",
    label: "Évaluation libre",
    sub: "Découverte sans engagement",
    desc: "Activation simple : recevez les dossiers qualifiés et décidez d'évoluer plus tard.",
    badge: "Recommandé pour démarrer",
    amount: 0,
    suffix: "€ — sans frais d'inscription",
  },
  {
    key: "lead_fee",
    label: "Mise en relation",
    sub: "Facturation par dossier qualifié",
    desc: "Vous ne payez qu'à la transmission d'un dossier qualifié et accepté.",
    amountFrom: "lead_fee",
  },
  {
    key: "monthly_subscription",
    label: "Référencement mensuel",
    sub: "Profil référencé public",
    desc: "Accès complet à la plateforme et flux régulier de dossiers.",
    amountFrom: "monthly_subscription",
  },
  {
    key: "premium_visibility",
    label: "Visibilité premium",
    sub: "Mise en avant éditoriale",
    desc: "Référencement prioritaire et publications institutionnelles.",
    amountFrom: "premium_visibility",
    featured: true,
  },
];

export default function Claim() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { spécialiste, invitation, draft, affiliation_pricing, regulated_professions }
  const [stepIndex, setStepIndex] = useState(0);

  // Form state
  const [contactEmail, setContactEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCodeHint, setDevCodeHint] = useState(null);
  const [codeSending, setCodeSending] = useState(false);
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const [identity, setIdentity] = useState({
    full_name: "",
    profession: "",
    structure: "",
    city: "",
    declared_specialties: "",
    biography: "",
    siret: "",
    ordre_number: "",
    ordre_region: "",
    contact_phone: "",
    website: "",
  });

  const [justificatif, setJustificatif] = useState(null); // {id, filename, size}
  const [uploading, setUploading] = useState(false);
  const [planKey, setPlanKey] = useState("evaluation_libre");
  const [consentCgu, setConsentCgu] = useState(false);
  const [consentData, setConsentData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // -------- Load
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiClient.get(`/claim/${token}`);
        if (!mounted) return;
        setData(res.data);
        setEmailVerified(!!res.data.draft?.email_verified);
        setSubmitted(!!res.data.draft?.submitted);
        if (res.data.expert?.display_name) {
          setIdentity((s) => ({ ...s, full_name: res.data.expert.display_name }));
        }
        if (res.data.expert?.profession) {
          setIdentity((s) => ({ ...s, profession: res.data.expert.profession }));
        }
        if (res.data.expert?.structure) {
          setIdentity((s) => ({ ...s, structure: res.data.expert.structure }));
        }
        if (res.data.expert?.city) {
          setIdentity((s) => ({ ...s, city: res.data.expert.city }));
        }
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail) || "Lien invalide.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const regulated = useMemo(() => {
    const list = (data?.regulated_professions || []).map((s) => s.toLowerCase());
    const prof = (identity.profession || "").toLowerCase();
    return list.some((r) => prof.includes(r));
  }, [identity.profession, data]);

  const handleSendCode = async () => {
    if (!contactEmail) return toast.error("Veuillez renseigner votre email professionnel.");
    setCodeSending(true);
    try {
      const res = await apiClient.post(`/claim/${token}/email-code/send`, { contact_email: contactEmail });
      toast.success(res.data.message);
      if (res.data.dev_code) {
        setDevCodeHint(res.data.dev_code);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Envoi impossible.");
    } finally {
      setCodeSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length < 4) return toast.error("Veuillez saisir le code reçu.");
    setCodeVerifying(true);
    try {
      await apiClient.post(`/claim/${token}/email-code/verify`, { code });
      setEmailVerified(true);
      toast.success("Email vérifié avec succès.");
      setStepIndex(2);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Code invalide.");
    } finally {
      setCodeVerifying(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      return toast.error("Fichier trop volumineux (8 Mo max).");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "justificatif");
      const res = await apiClient.post(`/claim/${token}/justificatif`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJustificatif(res.data);
      toast.success("Justificatif transmis.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload impossible.");
    } finally {
      setUploading(false);
    }
  };

  const canSubmit =
    emailVerified &&
    identity.full_name.trim().length >= 2 &&
    identity.profession.trim().length >= 2 &&
    contactEmail &&
    consentCgu &&
    consentData &&
    (regulated ? !!identity.ordre_number.trim() : !!(identity.siret || identity.ordre_number).trim());

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Veuillez compléter tous les champs requis et accepter les consentements.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...identity,
        declared_specialties: identity.declared_specialties
          .split(",").map((s) => s.trim()).filter(Boolean),
        contact_email: contactEmail,
        plan_key: planKey,
        consent_cgu: consentCgu,
        consent_data_processing: consentData,
      };
      await apiClient.post(`/claim/${token}/submit`, payload);
      setSubmitted(true);
      setStepIndex(STEPS.length - 1);
      toast.success("Revendication transmise. L'équipe CONFILEX vous contactera sous 48-72h.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Soumission impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  // -------- Render guards
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center text-[#0B3A2A]">
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> Chargement de votre invitation…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] text-[#0B3A2A]">
        <Navbar variant="light" />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <AlertTriangle className="w-10 h-10 text-[#C9A961] mx-auto mb-4" />
          <h1 className="font-serif italic text-3xl mb-3">Lien d'invitation indisponible</h1>
          <p className="text-slate-600">{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  const expert = data.expert;
  const pricing = data.affiliation_pricing || {};
  const currentStep = STEPS[stepIndex];

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#0B3A2A]">
      <Seo title="Revendication de fiche · CONFILEX" description="Activez votre profil spécialiste sur CONFILEX." />
      <Navbar variant="light" />

      {/* HERO */}
      <section className="relative pt-24 pb-10 overflow-hidden">
        <ArtBackdrop
          src="/art/sorola/promenade-2.jpg"
          opacity={0.16}
          blur={26}
          saturate={0.65}
          overlay="linear-gradient(180deg, rgba(250,247,241,0.86) 0%, rgba(250,247,241,0.96) 80%)"
        />
        <div className="relative max-w-5xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-4">
            Tunnel d'affiliation · {expert.display_name}
          </p>
          <h1 className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-4">
            Activez votre fiche professionnelle.
          </h1>
          <p className="text-slate-700 leading-relaxed max-w-2xl">
            Vous avez été identifié comme professionnel pouvant rejoindre le réseau CONFILEX.
            Quelques minutes suffisent pour vérifier votre identité, choisir vos conditions et activer votre espace.
          </p>
          <div className="mt-6 p-4 rounded-lg bg-white/70 border border-[#0B3A2A]/10 text-xs text-slate-600 max-w-2xl">
            <strong className="text-[#0B3A2A]">Invitation reçue de :</strong>{" "}
            {data.invitation.invited_by_email_masked || "Équipe CONFILEX"}
            {data.invitation.message && (
              <p className="mt-2 italic text-slate-700">« {data.invitation.message} »</p>
            )}
          </div>
        </div>
      </section>

      {/* STEPPER */}
      <section className="bg-white border-y border-[#0B3A2A]/10">
        <div className="max-w-5xl mx-auto px-6 py-6 flex gap-2 overflow-x-auto">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === stepIndex;
            const done = i < stepIndex || (s.id === "email" && emailVerified) || submitted;
            return (
              <div
                key={s.id}
                data-testid={`claim-step-${s.id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg shrink-0 ${
                  active
                    ? "bg-[#0B3A2A] text-white"
                    : done
                    ? "bg-[#C9A961]/15 text-[#0B3A2A]"
                    : "bg-slate-50 text-slate-500"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="text-xs font-medium uppercase tracking-wide">{s.label}</span>
                {done && !active && <Check className="w-3 h-3 text-[#C9A961]" />}
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white border border-[#0B3A2A]/10 rounded-2xl p-8 shadow-sm">
            {/* INTRO */}
            {currentStep.id === "intro" && (
              <StepIntro expert={expert} onNext={() => setStepIndex(1)} />
            )}

            {/* EMAIL */}
            {currentStep.id === "email" && (
              <StepEmail
                contactEmail={contactEmail}
                setContactEmail={setContactEmail}
                code={code}
                setCode={setCode}
                devCodeHint={devCodeHint}
                emailVerified={emailVerified}
                codeSending={codeSending}
                codeVerifying={codeVerifying}
                onSend={handleSendCode}
                onVerify={handleVerifyCode}
              />
            )}

            {/* IDENTITY */}
            {currentStep.id === "identity" && (
              <StepIdentity identity={identity} setIdentity={setIdentity} regulated={regulated} />
            )}

            {/* JUSTIFICATIF */}
            {currentStep.id === "justificatif" && (
              <StepJustificatif
                justificatif={justificatif}
                uploading={uploading}
                onUpload={handleUpload}
                regulated={regulated}
              />
            )}

            {/* PLAN */}
            {currentStep.id === "plan" && (
              <StepPlan planKey={planKey} setPlanKey={setPlanKey} pricing={pricing} />
            )}

            {/* REVIEW */}
            {currentStep.id === "review" && (
              <StepReview
                identity={identity}
                contactEmail={contactEmail}
                planKey={planKey}
                pricing={pricing}
                consentCgu={consentCgu}
                setConsentCgu={setConsentCgu}
                consentData={consentData}
                setConsentData={setConsentData}
                onSubmit={handleSubmit}
                canSubmit={canSubmit}
                submitting={submitting}
                submitted={submitted}
              />
            )}

            {/* NAV */}
            {!submitted && (
              <div className="mt-10 flex items-center justify-between">
                <button
                  type="button"
                  data-testid="claim-prev-btn"
                  disabled={stepIndex === 0}
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B3A2A] disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" /> Précédent
                </button>
                {stepIndex < STEPS.length - 1 ? (
                  <button
                    type="button"
                    data-testid="claim-next-btn"
                    onClick={() => {
                      if (currentStep.id === "email" && !emailVerified) {
                        return toast.error("Veuillez vérifier votre email avant de continuer.");
                      }
                      setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
                    }}
                    className="inline-flex items-center gap-2 bg-[#0B3A2A] text-white px-5 py-2.5 rounded-lg hover:brightness-110 transition text-sm"
                  >
                    Continuer <ChevronRight className="w-4 h-4" />
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {/* DISCLAIMER */}
          <p className="text-xs text-slate-500 italic text-center mt-8 max-w-2xl mx-auto leading-relaxed">
            {expert.legal_disclaimer}
            <br />
            Vos données sont traitées dans le strict respect du RGPD et des règles déontologiques de votre profession.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================
function StepIntro({ expert, onNext }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Étape 1 · Bienvenue</p>
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
        Bonjour {expert.display_name},
      </h2>
      <p className="text-sm text-slate-700 leading-relaxed mb-5">
        Votre profil a été identifié par notre moteur d'orientation comme pouvant correspondre à
        certaines demandes d'utilisateurs CONFILEX. Vous n'êtes pas encore affilié à la plateforme.
      </p>
      <div className="rounded-lg bg-[#FAF7F1] border border-[#0B3A2A]/10 p-4 mb-5">
        <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Fiche actuelle</p>
        <p className="font-medium text-[#0B3A2A]">{expert.display_name}</p>
        <p className="text-sm text-slate-600 mt-1">
          {[expert.profession, expert.structure, expert.city].filter(Boolean).join(" · ") || "Profession à compléter"}
        </p>
      </div>
      <ul className="space-y-2 text-sm text-slate-700">
        {[
          "Vérification de votre email professionnel",
          "Confirmation de votre identité professionnelle (SIRET, n° d'ordre)",
          "Upload optionnel d'un justificatif",
          "Choix libre de votre plan d'affiliation",
          "Activation après contrôle CONFILEX sous 48-72h ouvrées",
        ].map((t) => (
          <li key={t} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#C9A961] shrink-0 mt-0.5" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid="claim-intro-start"
        onClick={onNext}
        className="mt-8 inline-flex items-center gap-2 bg-[#0B3A2A] text-white px-5 py-2.5 rounded-lg hover:brightness-110 text-sm"
      >
        Commencer <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function StepEmail({
  contactEmail, setContactEmail, code, setCode, devCodeHint,
  emailVerified, codeSending, codeVerifying, onSend, onVerify,
}) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Étape 2 · Vérification</p>
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">Vérifiez votre email professionnel</h2>
      <p className="text-sm text-slate-600 mb-6">
        Nous envoyons un code à 6 chiffres pour confirmer que vous êtes bien le titulaire de cette adresse.
      </p>

      <label className="block mb-2 text-xs uppercase tracking-wider text-slate-500">Email professionnel</label>
      <div className="flex gap-2 mb-3">
        <input
          type="email"
          data-testid="claim-email-input"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          disabled={emailVerified}
          placeholder="cabinet@exemple.fr"
          className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961] disabled:bg-slate-50"
        />
        <button
          type="button"
          data-testid="claim-send-code-btn"
          disabled={emailVerified || codeSending || !contactEmail}
          onClick={onSend}
          className="px-4 py-2.5 bg-[#0B3A2A] text-white text-sm rounded-lg hover:brightness-110 disabled:opacity-40"
        >
          {codeSending ? <Loader2 className="w-4 h-4 animate-spin" /> : (emailVerified ? "Vérifié" : "Envoyer le code")}
        </button>
      </div>

      {devCodeHint && !emailVerified && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          <strong>Mode démonstration :</strong> votre code est <span className="font-mono">{devCodeHint}</span> (en attendant l'activation Resend, le code est affiché ici).
        </p>
      )}

      {!emailVerified && (
        <>
          <label className="block mb-2 text-xs uppercase tracking-wider text-slate-500">Code à 6 chiffres</label>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              data-testid="claim-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm font-mono tracking-widest text-center focus:outline-none focus:border-[#C9A961]"
            />
            <button
              type="button"
              data-testid="claim-verify-code-btn"
              disabled={codeVerifying || code.length < 4}
              onClick={onVerify}
              className="px-4 py-2.5 bg-[#C9A961] text-white text-sm rounded-lg hover:brightness-110 disabled:opacity-40"
            >
              {codeVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider"}
            </button>
          </div>
        </>
      )}

      {emailVerified && (
        <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2">
          <Check className="w-4 h-4" /> Email vérifié — vous pouvez continuer.
        </div>
      )}
    </div>
  );
}

function StepIdentity({ identity, setIdentity, regulated }) {
  const set = (k) => (e) => setIdentity((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Étape 3 · Identité professionnelle</p>
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">Confirmez votre identité</h2>
      <p className="text-sm text-slate-600 mb-6">
        Les informations ci-dessous nous permettent de garantir l'authenticité de votre profil auprès des utilisateurs.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nom complet *" value={identity.full_name} onChange={set("full_name")} testid="claim-fullname" />
        <Field label="Profession *" value={identity.profession} onChange={set("profession")}
               placeholder="Avocat, Expert-comptable, Notaire…" testid="claim-profession" />
        <Field label="Structure / Cabinet" value={identity.structure} onChange={set("structure")} testid="claim-structure" />
        <Field label="Ville" value={identity.city} onChange={set("city")} testid="claim-city" />
        <Field label="SIRET / SIREN" value={identity.siret} onChange={set("siret")}
               placeholder="14 chiffres" testid="claim-siret" />
        <Field
          label={regulated ? "N° d'ordre / barreau *" : "N° d'ordre / barreau (si applicable)"}
          value={identity.ordre_number}
          onChange={set("ordre_number")}
          placeholder="ex : N° toque, RPPS, OEC…"
          testid="claim-ordre-number"
        />
        <Field label="Ressort / Région d'inscription" value={identity.ordre_region} onChange={set("ordre_region")}
               placeholder="Barreau de Paris, OEC Lyon…" testid="claim-ordre-region" />
        <Field label="Téléphone" value={identity.contact_phone} onChange={set("contact_phone")} testid="claim-phone" />
        <Field label="Site web" value={identity.website} onChange={set("website")} placeholder="https://" testid="claim-website" />
        <Field label="Spécialités (séparées par virgule)" value={identity.declared_specialties}
               onChange={set("declared_specialties")} placeholder="Fiscalité, M&A, Contentieux" testid="claim-specialties" full />
      </div>
      <label className="block mt-4 text-xs uppercase tracking-wider text-slate-500 mb-2">
        Présentation (facultatif)
      </label>
      <textarea
        data-testid="claim-biography"
        rows={4}
        value={identity.biography}
        onChange={(e) => setIdentity((s) => ({ ...s, biography: e.target.value }))}
        placeholder="Une présentation institutionnelle, sobre, de votre pratique."
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961]"
      />
      {regulated && (
        <p className="mt-4 text-xs text-[#0B3A2A] bg-[#C9A961]/10 border border-[#C9A961]/30 rounded p-3">
          <Sparkles className="inline w-3.5 h-3.5 text-[#C9A961] mr-1" />
          Profession réglementée détectée : le numéro d'ordre est obligatoire.
        </p>
      )}
    </div>
  );
}

function StepJustificatif({ justificatif, uploading, onUpload, regulated }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Étape 4 · Justificatif</p>
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">
        Justificatif professionnel {regulated ? "" : "(facultatif)"}
      </h2>
      <p className="text-sm text-slate-600 mb-6">
        Vous pouvez transmettre un justificatif pour accélérer la vérification : carte professionnelle,
        attestation d'inscription à l'ordre, Kbis, RPPS, etc. (PDF, JPG, PNG — 8 Mo max).
      </p>

      <label
        htmlFor="claim-justificatif-input"
        className="flex flex-col items-center justify-center border-2 border-dashed border-[#0B3A2A]/15 rounded-xl p-10 cursor-pointer hover:border-[#C9A961]/40 transition bg-[#FAF7F1]/60"
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#C9A961]" />
        ) : justificatif ? (
          <>
            <FileCheck2 className="w-8 h-8 text-emerald-600 mb-2" />
            <p className="text-sm font-medium text-[#0B3A2A]">{justificatif.filename}</p>
            <p className="text-xs text-slate-500">
              {(justificatif.size / 1024).toFixed(1)} Ko — Cliquez pour remplacer
            </p>
          </>
        ) : (
          <>
            <Upload className="w-7 h-7 text-[#C9A961] mb-3" />
            <p className="text-sm text-[#0B3A2A] font-medium">Glissez-déposez ou cliquez pour sélectionner</p>
            <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG — 8 Mo max</p>
          </>
        )}
        <input
          id="claim-justificatif-input"
          data-testid="claim-justificatif-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
      </label>

      <p className="text-xs text-slate-500 mt-4 italic">
        Vous pouvez sauter cette étape — un membre de l'équipe pourra revenir vers vous si nécessaire.
      </p>
    </div>
  );
}

function StepPlan({ planKey, setPlanKey, pricing }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Étape 5 · Affiliation</p>
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">Choisissez votre plan</h2>
      <p className="text-sm text-slate-600 mb-6">
        Aucune contrepartie financière n'est imposée sans votre accord explicite. Vous pourrez faire évoluer votre plan à tout moment.
      </p>
      <div className="space-y-3">
        {PLAN_DEFS.map((p) => {
          const selected = planKey === p.key;
          const amount = p.amount !== undefined ? p.amount : pricing[p.amountFrom]?.amount_eur;
          const suffix = p.suffix || (pricing[p.amountFrom]?.amount_pct
            ? `${pricing[p.amountFrom].amount_pct} %`
            : "€ HT / mois");
          return (
            <label
              key={p.key}
              data-testid={`claim-plan-${p.key}`}
              className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition ${
                selected
                  ? "border-[#C9A961] bg-[#C9A961]/5 shadow-sm"
                  : "border-slate-200 bg-white hover:border-[#0B3A2A]/20"
              }`}
            >
              <input
                type="radio"
                name="plan"
                checked={selected}
                onChange={() => setPlanKey(p.key)}
                className="mt-1 accent-[#C9A961]"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-medium text-[#0B3A2A]">{p.label}</span>
                  {p.badge && (
                    <span className="text-[10px] uppercase tracking-wider bg-[#0B3A2A] text-[#C9A961] px-2 py-0.5 rounded">
                      {p.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-1">{p.sub}</p>
                <p className="text-sm text-slate-700 mb-2">{p.desc}</p>
                <p className="text-sm font-serif text-[#0B3A2A]">
                  {amount !== undefined ? (
                    <>
                      <span className="text-xl tabular-nums">{amount}</span>{" "}
                      <span className="text-xs text-slate-500">{p.amount === 0 ? p.suffix : suffix}</span>
                    </>
                  ) : (
                    <span className="text-xs text-slate-500">Tarif sur mesure</span>
                  )}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function StepReview({
  identity, contactEmail, planKey, pricing, consentCgu, setConsentCgu,
  consentData, setConsentData, onSubmit, canSubmit, submitting, submitted,
}) {
  if (submitted) {
    return (
      <div className="text-center py-6" data-testid="claim-success">
        <Check className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
        <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-3">Revendication transmise</h2>
        <p className="text-slate-600 max-w-md mx-auto mb-4 leading-relaxed">
          Merci ! Votre dossier a bien été reçu. Notre équipe procède à la vérification de votre identité
          sous 48 à 72h ouvrées. Vous recevrez un email à la validation de votre fiche.
        </p>
        <p className="text-xs text-slate-500 italic">
          Vous pouvez fermer cette page en toute sécurité.
        </p>
      </div>
    );
  }

  const planDef = PLAN_DEFS.find((p) => p.key === planKey);
  const amount = planDef?.amount !== undefined ? planDef.amount : pricing[planDef?.amountFrom]?.amount_eur;

  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Étape 6 · Validation</p>
      <h2 className="font-serif italic text-2xl text-[#0B3A2A] mb-4">Récapitulatif & consentement</h2>

      <dl className="grid sm:grid-cols-2 gap-4 mb-6 text-sm">
        <Row label="Nom" value={identity.full_name} />
        <Row label="Profession" value={identity.profession} />
        <Row label="Structure" value={identity.structure} />
        <Row label="Ville" value={identity.city} />
        <Row label="Email vérifié" value={contactEmail} />
        <Row label="SIRET" value={identity.siret || "—"} />
        <Row label="N° ordre/barreau" value={identity.ordre_number || "—"} />
        <Row label="Plan choisi" value={`${planDef?.label} (${amount ?? "—"} €)`} />
      </dl>

      <div className="space-y-3 mb-6">
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="claim-consent-cgu"
            checked={consentCgu}
            onChange={(e) => setConsentCgu(e.target.checked)}
            className="mt-1 accent-[#C9A961]"
          />
          <span>
            J'accepte les <a href="/legal" target="_blank" rel="noreferrer" className="text-[#0B3A2A] underline">
            Conditions Générales d'Affiliation</a> de CONFILEX et déclare exercer dans le respect
            des règles déontologiques de ma profession.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="claim-consent-data"
            checked={consentData}
            onChange={(e) => setConsentData(e.target.checked)}
            className="mt-1 accent-[#C9A961]"
          />
          <span>
            J'autorise CONFILEX à traiter mes données personnelles et professionnelles dans le cadre
            strict de cette affiliation, conformément au RGPD.
          </span>
        </label>
      </div>

      <button
        type="button"
        data-testid="claim-submit-btn"
        disabled={!canSubmit || submitting}
        onClick={onSubmit}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#0B3A2A] text-white px-5 py-3 rounded-lg hover:brightness-110 disabled:opacity-40 text-sm"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Soumettre ma revendication
      </button>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
      <input
        type="text"
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961]"
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="bg-[#FAF7F1]/70 border border-slate-100 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="text-sm font-medium text-[#0B3A2A] truncate">{value || "—"}</p>
    </div>
  );
}
